import { useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { ArrowRight } from '@mint-lib/icons';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogRoot,
  DialogTitle,
  Divider,
  Switch,
  SwitchLabel,
  typographyVariants,
} from '@mint-lib/tw-components';
import { Alert, ProgressCircular } from '@mint-lib/tw-components/patterns';
import type { MediaPlan } from '@mint-sdk/api-media-plan';

import { useCurrencyRateApi } from '../../../../../../api/queries/useCurrencyRateApi.js';
import type { ModalDataType } from '../../../../../../hooks/useSetConfirmationCurrency.js';

// The exchange-rate type returned when no manual rate is configured and the rate
// is obtained from global sources.
const GLOBAL_RATE_TYPE = 'monthly-average';

export const ConfirmCurrencyModal = ({
  modalData,
  onClose,
}: {
  modalData: ModalDataType;
  onClose: () => void;
}) => {
  const { t } = useTranslation('@myn/planning_v2');

  const [convertValue, setConvertValue] = useState<boolean>(true);

  const {
    oldValue,
    newValue,
    api,
    data,
    colDef,
    node,
    updateColumnWithErrorHandling,
  } = modalData;

  const options = colDef.cellRendererParams
    .options as MediaPlan.Column['options'];

  const fromCurrency = Array.isArray(oldValue) ? oldValue[0] : (oldValue ?? '');
  const fromCurrencyLabel = options?.find(
    (option) => option.value === fromCurrency,
  )?.label;

  const toCurrency = Array.isArray(newValue) ? newValue[0] : (newValue ?? '');
  const toCurrencyLabel = options?.find(
    (option) => option.value === toCurrency,
  )?.label;

  const mediaItemId = modalData.data.mediaItemId;

  const { data: rateData, isPending: isCurrencyRatePending } =
    useCurrencyRateApi({
      fromCurrency,
      toCurrency,
      mediaItemId,
    });

  const isGlobalRate = rateData?.rateType === GLOBAL_RATE_TYPE;

  const handleConfirm = () => {
    if (!colDef.colId) return;
    const field = colDef.colId;
    const rowId = data.id;
    const value = toCurrency;
    updateColumnWithErrorHandling({
      field,
      value,
      rowId,
    });
    onClose();
  };

  const undoValue = () => {
    if (!node?.id || !colDef.colId) return;

    const dataWithOldValue = {
      ...node.data,
      attributes: {
        ...node.data?.attributes,
        [colDef.colId]: {
          ...node.data?.attributes[colDef.colId],
          value: oldValue,
        },
      },
    };

    if (!dataWithOldValue) return;

    api.applyServerSideTransaction({
      route: node.parent?.getRoute(),
      update: [dataWithOldValue],
    });
  };

  const handleCancel = () => {
    undoValue();
    onClose();
  };

  const handleOnOpenChange = (open: boolean) => {
    if (!open) {
      undoValue();
      onClose();
    }
  };

  return (
    <Dialog modal open={!!modalData} onOpenChange={handleOnOpenChange}>
      <DialogOverlay />
      <DialogRoot size="s">
        <DialogHeader>
          <DialogTitle>
            <span className={typographyVariants({ variant: 'h3' })}>
              {t('Change currency')}
            </span>
          </DialogTitle>
        </DialogHeader>
        <DialogContent>
          <div className="flex w-full flex-col gap-4">
            <div className="flex flex-col gap-1">
              <span className={typographyVariants({ variant: 'bodyLong01' })}>
                {t('By proceeding, you will change the media row currency')}
              </span>
              <h6
                className={typographyVariants({
                  variant: 'h6',
                  className: 'flex items-center gap-1',
                })}
              >
                {fromCurrencyLabel}
                <ArrowRight />
                {toCurrencyLabel}
              </h6>
            </div>

            <Divider className="my-4" />

            <SwitchLabel className="flex items-center gap-2">
              <Switch
                size="small"
                checked={convertValue}
                onCheckedChange={setConvertValue}
                aria-label="Convert value"
              />
              <span className={typographyVariants({ variant: 'bodyShort01' })}>
                {t('Convert value')}
              </span>
            </SwitchLabel>

            {convertValue &&
              (isCurrencyRatePending ? (
                <ProgressCircular className="m-auto mt-2" />
              ) : (
                <div className="flex flex-col gap-3">
                  <div className="my-4 flex flex-col">
                    <h5 className={typographyVariants({ variant: 'h5' })}>
                      {t('Exchange rate')}
                    </h5>
                    <span
                      className={typographyVariants({ variant: 'bodyLong01' })}
                    >
                      {`1 ${fromCurrencyLabel} = ${rateData?.rate} ${toCurrencyLabel}`}
                    </span>
                  </div>

                  {isGlobalRate && (
                    <Alert
                      severity="info"
                      variant="multiline"
                      removable={false}
                      title={t('Global exchange rate applied')}
                      description={t(
                        'No exchange rate is set in currency settings. It will be obtained from global sources.',
                      )}
                    />
                  )}
                </div>
              ))}
          </div>
        </DialogContent>
        <DialogFooter>
          <Button
            size="large"
            variant="text"
            aria-label={t('Cancel currency change')}
            onClick={handleCancel}
          >
            {t('Cancel')}
          </Button>
          <Button
            size="large"
            onClick={handleConfirm}
            disabled={convertValue && isCurrencyRatePending}
            aria-label={t('Confirm currency change')}
          >
            {t('Confirm')}
          </Button>
        </DialogFooter>
      </DialogRoot>
    </Dialog>
  );
};
